/**
 * Lazy Relations System
 *
 * This file contains lazy schema declarations to break circular dependencies
 * between entity schemas. Each entity that participates in circular relations
 * has a corresponding lazy schema that can be used in place of z.any().
 *
 * Usage:
 *   import { LazyUserRelation, LazyBotConfigRelation } from './relations';
 *
 *   // Instead of z.any()
 *   user: LazyUserRelation.optional(),
 *   bot_configs: z.array(LazyBotConfigRelation).optional(),
 */
import { z } from "zod";
import { ChargeRiskLevelSchema, CurrencyCodeSchema, DynamicFeeBracketSchema, ExchangeAccountStatusSchema, ExchangePlatformSchema, ExchangeTypeSchema, InvoiceDirectionSchema, InvoiceSeriesSchema, InvoiceStatusSchema, InvoiceTypeCodeSchema, InvoiceTypeSchema, PaymentMethodDetailsSchema, PaymentProcessorSchema, ReferrerTypeSchema, SeveritySchema, StrapiEntitySchema, StrapiIdSchema, StrapiMediaSchema, SubscriptionStatusSchema, TradeStateSchema, VerifactuStatusSchema, } from "./common.schemas";
// ============================================================================
// Forward Declarations (minimal relation shapes)
// ============================================================================
/**
 * Minimal user relation shape for when user is a relation field.
 * Contains fields commonly accessed when user is populated.
 */
export const UserRelationSchema = StrapiEntitySchema.extend({
    email: z.string(),
    username: z.string().nullish(),
    uuid: z.string().nullish(),
    name: z.string().nullish(),
    country: z.string().nullish(),
    mainCurrency: CurrencyCodeSchema.nullish(),
    language: z.string().nullish(),
    confirmed: z.boolean().nullish(),
    blocked: z.boolean().nullish(),
    isVIP: z.boolean().nullish(),
    isTest: z.boolean().nullish(),
    isManualPayment: z.boolean().nullish(),
    manualPaymentReason: z.string().nullish(),
    referrerType: ReferrerTypeSchema.nullish(),
    hasAcceptedAffiliateTnC: z.boolean().nullish(),
    isBlockedFromAffiliate: z.boolean().nullish(),
    systemFee: z.number().nullish(),
    // Computed field added by referral extend-populate middleware
    isActive: z.boolean().nullish(),
    // pm_* fields (injected by payments-manager plugin)
    pm_revolut_customerId: z.string().nullish(),
    // Nested relations are kept as minimal shapes or lazy references
});
/**
 * User role relation shape
 */
export const UserRoleRelationSchema = z.object({
    id: StrapiIdSchema,
    name: z.string(),
    type: z.string().nullish(),
});
/**
 * Exchange server schema
 */
export const ExchangeServerSchema = z.object({
    id: StrapiIdSchema,
    url: z.string(),
});
/**
 * Trading asset schema
 */
export const TradingAssetSchema = StrapiEntitySchema.extend({
    symbol: z.string(),
    name: z.string().nullish(),
    type: z.string().nullish(),
});
/**
 * Leverage options constraint schema
 */
export const LeverageOptionsConstraintSchema = z.enum(["only_30", "only_1k"]);
/**
 * Exchange schema - full entity
 */
export const ExchangeSchema = StrapiEntitySchema.extend({
    name: z.string(),
    type: ExchangeTypeSchema.nullish(),
    platform: ExchangePlatformSchema.nullish(),
    link: z.string().nullish(),
    leverageOptionsConstraint: LeverageOptionsConstraintSchema.nullish(),
    servers: z.array(ExchangeServerSchema).nullish(),
    trading_assets: z.array(TradingAssetSchema).nullish(),
});
/**
 * Minimal bot config relation shape
 */
export const BotConfigRelationSchema = StrapiEntitySchema.extend({
    uuid: z.string().nullish(),
    disabled: z.boolean().nullish(),
    lastTradesUpdate: z.string().nullish(),
    stats: z
        .object({
        username: z.string().nullish(),
        platform: z.string().nullish(),
        baseCurrency: CurrencyCodeSchema.nullish(),
        contractSize: z.string().nullish(),
        leverage: z.string().nullish(),
        drawdown: z.number().nullish(),
        profit_percentage: z.number().nullish(),
        absolute_profit: z.number().nullish(),
        trades: z.number().nullish(),
        openTrades: z.number().nullish(),
        balance: z.number().nullish(),
        equity: z.number().nullish(),
        stopLoss: z.number().nullish(),
        timestamp: z.string().nullish(),
        status: z.string().nullish(),
        expected_profit: z.number().nullish(),
    })
        .nullish(),
});
/**
 * Minimal exchange account relation shape
 */
export const ExchangeAccountRelationSchema = StrapiEntitySchema.extend({
    uuid: z.string().nullish(),
    accountId: z.string().nullish(),
    name: z.string().nullish(),
    status: ExchangeAccountStatusSchema.nullish(),
    error: z.string().nullish(),
    hidden: z.boolean().nullish(),
    defaultLeverage: z.number().nullish(),
    // Populated relations
    bot_configs: z.array(z.lazy(() => BotConfigRelationSchema)).nullish(),
    exchange: ExchangeSchema.nullish(),
    user: UserRelationSchema.nullish(),
});
/**
 * Minimal strategy relation shape
 */
export const StrategyRelationSchema = StrapiEntitySchema.extend({
    name: z.string(),
    uuid: z.string().nullish(),
    exchangeType: ExchangeTypeSchema.nullish(),
    exclusive: z.boolean().nullish(),
    details: z
        .object({
        description: z.string().nullish(),
        shortDescription: z.string().nullish(),
        minBalance: z.number().nullish(),
        maxBalance: z.number().nullish(),
        minLeverage: z.number().nullish(),
    })
        .nullish(),
    logo: StrapiMediaSchema.nullish(),
});
/**
 * Minimal subscription relation shape
 */
export const SubscriptionRelationSchema = StrapiEntitySchema.extend({
    // pm_* fields (injected by payments-manager plugin)
    pm_paymentProcessor: PaymentProcessorSchema.nullish(),
    pm_status: SubscriptionStatusSchema.nullish(),
    pm_currentPeriodStart: z.string().nullish(),
    pm_currentPeriodEnd: z.string().nullish(),
    pm_failureReason: z.string().nullish(),
    pm_failureCount: z.number().nullish(),
    pm_lastRetryAt: z.string().nullish(),
    pm_adminAttentionNeeded: z.boolean().nullish(),
    pm_lastManualRetryAt: z.string().nullish(),
    // Payment method relation - populated when subscription.pm_paymentMethod is requested
    pm_paymentMethod: z.lazy(() => PaymentMethodRelationSchema).nullish(),
});
/**
 * Minimal subscription tier relation shape
 */
export const SubscriptionTierRelationSchema = StrapiEntitySchema.extend({
    name: z.string(),
    code: z.string().nullish(),
    description: z.string().nullish(),
    monthlyPrice: z.number().nullish(),
    currency: CurrencyCodeSchema.nullish(),
    features: z.record(z.string(), z.unknown()).nullish(),
    isDefault: z.boolean().nullish(),
    isActive: z.boolean().nullish(),
    sortOrder: z.number().nullish(),
    vpsOverride: z.number().nullish(),
    vpsText: z.string().nullish(),
    tierText: z.string().nullish(),
    profitFeeText: z.string().nullish(),
    discountText: z.string().nullish(),
    dynamicFee: z.array(DynamicFeeBracketSchema).nullish(),
    commissionOnlyBilling: z.boolean().nullish(),
});
/**
 * Minimal charge relation shape
 */
export const ChargeRelationSchema = StrapiEntitySchema.extend({
    periodProfit: z.number().nullish(),
    profitCurrency: CurrencyCodeSchema.nullish(),
    commission: z.number().nullish(),
    discounts: z.number().nullish(),
    discountedAmount: z.number().nullish(),
    baseAmount: z.number().nullish(),
    percentageFee: z.number().nullish(),
    vpsCost: z.number().nullish(),
    tierCost: z.number().nullish(),
    profitFee: z.number().nullish(),
    freeDays: z.number().nullish(),
    billableDays: z.number().nullish(),
    paymentType: z.enum(["automatic", "manual"]).nullish(),
    paymentLink: z.string().nullish(),
    paymentLinkExpiry: z.string().nullish(),
    revolutOrderId: z.string().nullish(),
    verifactu_id: z.number().nullish(),
    verifactu_status: VerifactuStatusSchema.nullish(),
    verifactu_error: z.string().nullish(),
    // pm_* fields (injected by payments-manager plugin)
    pm_amount: z.number().nullish(),
    pm_currency: CurrencyCodeSchema.nullish(),
    pm_paymentProcessor: PaymentProcessorSchema.nullish(),
    pm_status: z.enum(["pending", "processing", "completed", "failed", "cancelled", "skipped"]).nullish(),
    skipReason: z.enum(["bot_inactive", "free_period_package", "no_profits", "fully_discounted"]).nullish(),
    pm_reason: z.string().nullish(),
    pm_bank_message: z.string().nullish(),
    pm_failureReason: z.string().nullish(),
    pm_refundReason: z.string().nullish(),
    pm_refunded: z.boolean().nullish(),
    pm_invoiceNumber: z.number().nullish(),
    pm_invoiceUrl: z.string().nullish(),
    pm_periodStart: z.string().nullish(),
    pm_periodEnd: z.string().nullish(),
    pm_risk_level: ChargeRiskLevelSchema.nullish(),
});
/**
 * Minimal refund relation shape
 */
export const RefundRelationSchema = StrapiEntitySchema.extend({
    verifactu_id: z.number().nullish(),
    verifactu_status: VerifactuStatusSchema.nullish(),
    verifactu_error: z.string().nullish(),
    // pm_* fields (injected by payments-manager plugin)
    pm_amount: z.number().nullish(),
    pm_currency: CurrencyCodeSchema.nullish(),
    pm_paymentProcessor: PaymentProcessorSchema.nullish(),
    // refund.pm_status enum matches Strapi: payments-manager/server/content-types/userDefinedOverrides.js
    // Refunds cannot be "skipped" (unlike charges) — do not add it here.
    pm_status: z.enum(["pending", "processing", "completed", "failed", "cancelled"]).nullish(),
    pm_reason: z.string().nullish(),
    pm_failureReason: z.string().nullish(),
    pm_refundReason: z.string().nullish(),
    pm_invoiceNumber: z.number().nullish(),
    pm_invoiceUrl: z.string().nullish(),
});
/**
 * Minimal trade relation shape
 * Note: API returns strings for price/timestamp/amount, and uses "amount" instead of "volume"
 */
export const TradeRelationSchema = StrapiEntitySchema.extend({
    positionId: z.string().nullish(),
    orderId: z.string().nullish(),
    dealId: z.string().nullish(),
    tradeId: z.string().nullish(),
    type: z.enum(["POSITION_TYPE_SELL", "POSITION_TYPE_BUY"]),
    symbol: z.string(),
    // API may return "amount" as string instead of "volume" as number
    volume: z.union([z.number(), z.string()]).nullish(),
    amount: z.union([z.number(), z.string()]).nullish(),
    // API returns price as string
    price: z.union([z.number(), z.string()]),
    // API returns timestamp as ISO string
    timestamp: z.union([z.number(), z.string()]),
    profit: z.union([z.number(), z.string()]).nullish(),
    swap: z.union([z.number(), z.string()]).nullish(),
    commission: z.union([z.number(), z.string()]).nullish(),
    stopLoss: z.union([z.number(), z.string()]).nullish(),
    takeProfit: z.union([z.number(), z.string()]).nullish(),
    state: TradeStateSchema.nullish(),
    closePrice: z.union([z.number(), z.string()]).nullish(),
    closeTimestamp: z.union([z.number(), z.string()]).nullish(),
    currency: CurrencyCodeSchema.nullish(),
    comment: z.string().nullish(),
});
/**
 * Minimal referral relation shape - includes referrer for user.referral.referrer population
 */
export const ReferralRelationSchema = StrapiEntitySchema.extend({
    expired: z.boolean().nullish(),
    // Use lazy to avoid circular dependency with UserRelationSchema
    referrer: z.lazy(() => UserRelationSchema).nullish(),
});
/**
 * Minimal referral code relation shape
 */
export const ReferralCodeRelationSchema = StrapiEntitySchema.extend({
    name: z.string().nullish(),
    code: z.string(),
});
/**
 * Minimal discount relation shape
 */
export const DiscountRelationSchema = StrapiEntitySchema.extend({
    amount: z.number().nullish(),
    currency: CurrencyCodeSchema.nullish(),
    expiresAt: z.string().nullish(),
    usedAt: z.string().nullish(),
});
/**
 * Minimal commission relation shape
 */
export const CommissionRelationSchema = StrapiEntitySchema.extend({
    amount: z.number(),
    currency: CurrencyCodeSchema,
    status: z.enum(["pending", "available", "claimed", "expired"]).nullish(),
});
/**
 * Minimal invoice relation shape
 */
export const InvoiceRelationSchema = StrapiEntitySchema.extend({
    invoiceNumber: z.string(),
    invoiceSeries: InvoiceSeriesSchema,
    invoiceType: InvoiceTypeCodeSchema,
    invoiceTypeCode: InvoiceTypeCodeSchema,
    direction: InvoiceDirectionSchema,
    type: InvoiceTypeSchema,
    status: InvoiceStatusSchema,
    baseAmount: z.number(),
    subtotal: z.number(),
    vatAmount: z.number(),
    irpfAmount: z.number(),
    netPayable: z.number(),
    totalAmount: z.number(),
    taxAmount: z.number(),
    total: z.number(),
    currency: CurrencyCodeSchema,
    invoiceDate: z.string().nullish(),
    issueDate: z.string().nullish(),
    dueDate: z.string().nullish(),
    paidAt: z.string().nullish(),
});
/**
 * Minimal payment relation shape (api::payment.payment)
 */
export const PaymentRelationSchema = StrapiEntitySchema.extend({
    pm_amount: z.number().nullish(),
    pm_currency: CurrencyCodeSchema.nullish(),
    pm_paymentProcessor: PaymentProcessorSchema.nullish(),
    pm_status: z.enum(["pending", "declined", "failed", "completed", "cancelled"]).nullish(),
    pm_bank_message: z.string().nullish(),
    pm_failureReason: z.string().nullish(),
    pm_risk_level: ChargeRiskLevelSchema.nullish(),
});
/**
 * Minimal payment method relation shape (plugin::payments-manager.payment-method)
 */
export const PaymentMethodRelationSchema = StrapiEntitySchema.extend({
    pm_type: z.enum(["card"]).nullish(),
    pm_paymentProcessor: PaymentProcessorSchema.nullish(),
    pm_isValid: z.boolean().nullish(),
    pm_processor_paymentMethodId: z.string().nullish(),
    pm_details: PaymentMethodDetailsSchema.nullish(),
});
/**
 * Minimal payout relation shape (plugin::payments-manager.payout)
 */
export const PayoutRelationSchema = StrapiEntitySchema.extend({
    pm_amount: z.number().nullish(),
    pm_currency: CurrencyCodeSchema.nullish(),
    pm_paymentProcessor: PaymentProcessorSchema.nullish(),
    pm_status: z.enum(["pending", "processing", "processed", "failed", "cancelled", "expired"]).nullish(),
    pm_reason: z.string().nullish(),
    pm_invoiceNumber: z.string().nullish(),
    pm_processor_payoutId: z.string().nullish(),
});
/**
 * Minimal billing data relation shape
 */
/**
 * Billing data is a Strapi component, not a content type.
 * Components have an id but no timestamps (createdAt/updatedAt).
 */
export const BillingDataRelationSchema = z.object({
    id: StrapiIdSchema,
    firstName: z.string().nullish(),
    lastName: z.string().nullish(),
    companyName: z.string().nullish(),
    address: z.string().nullish(),
    city: z.string().nullish(),
    state: z.string().nullish(),
    postalCode: z.string().nullish(),
    country: z.string().nullish(),
    vatNumber: z.string().nullish(),
    taxId: z.string().nullish(),
    email: z.string().nullish(),
    phone: z.string().nullish(),
});
/**
 * Minimal company relation shape
 */
export const CompanyRelationSchema = StrapiEntitySchema.extend({
    name: z.string(),
    taxId: z.string().nullish(),
    country: z.string().nullish(),
});
/**
 * Minimal free period package relation shape
 */
export const FreePeriodPackageRelationSchema = StrapiEntitySchema.extend({
    startDate: z.string().nullish(),
    endDate: z.string().nullish(),
    isActive: z.boolean().nullish(),
    durationDays: z.number().nullish(),
    reason: z.string().nullish(),
    grantType: z.enum(["admin", "registration", "promotion", "referral", "compensation", "migration"]).nullish(),
});
/**
 * Minimal bot warning relation shape
 */
export const BotWarningRelationSchema = StrapiEntitySchema.extend({
    type: z.string().nullish(),
    message: z.string().nullish(),
    severity: SeveritySchema.nullish(),
    dismissedAt: z.string().nullish(),
});
// ============================================================================
// Lazy Schema Factories
// ============================================================================
/**
 * Create a lazy reference to a schema.
 * Use these for relation fields to break circular dependencies.
 */
export const LazyUserRelation = z.lazy(() => UserRelationSchema);
export const LazyUserRoleRelation = z.lazy(() => UserRoleRelationSchema);
export const LazyExchangeRelation = z.lazy(() => ExchangeSchema);
export const LazyExchangeAccountRelation = z.lazy(() => ExchangeAccountRelationSchema);
export const LazyStrategyRelation = z.lazy(() => StrategyRelationSchema);
export const LazySubscriptionRelation = z.lazy(() => SubscriptionRelationSchema);
export const LazySubscriptionTierRelation = z.lazy(() => SubscriptionTierRelationSchema);
export const LazyBotConfigRelation = z.lazy(() => BotConfigRelationSchema);
export const LazyChargeRelation = z.lazy(() => ChargeRelationSchema);
export const LazyRefundRelation = z.lazy(() => RefundRelationSchema);
export const LazyTradeRelation = z.lazy(() => TradeRelationSchema);
export const LazyReferralRelation = z.lazy(() => ReferralRelationSchema);
export const LazyReferralCodeRelation = z.lazy(() => ReferralCodeRelationSchema);
export const LazyDiscountRelation = z.lazy(() => DiscountRelationSchema);
export const LazyCommissionRelation = z.lazy(() => CommissionRelationSchema);
export const LazyInvoiceRelation = z.lazy(() => InvoiceRelationSchema);
export const LazyPaymentRelation = z.lazy(() => PaymentRelationSchema);
export const LazyPaymentMethodRelation = z.lazy(() => PaymentMethodRelationSchema);
export const LazyPayoutRelation = z.lazy(() => PayoutRelationSchema);
export const LazyBillingDataRelation = z.lazy(() => BillingDataRelationSchema);
export const LazyCompanyRelation = z.lazy(() => CompanyRelationSchema);
export const LazyFreePeriodPackageRelation = z.lazy(() => FreePeriodPackageRelationSchema);
export const LazyBotWarningRelation = z.lazy(() => BotWarningRelationSchema);
